import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  deleteCardEffect,
  fetchCollection,
  fetchUnimplementedEffectCardIds,
  getCardEffect,
} from '../../services/api/apiService';
import { getProxiedCardImageUrl } from '../../features/utils';

const STATUS = {
  IMPLEMENTED: 'implemented',
  UNIMPLEMENTED: 'unimplemented',
  NO_EFFECT: 'no-effect',
};

export const EFFECT_SOURCE = {
  DB: 'db',
  CODE: 'code',
  NONE: 'none',
};

const EFFECT_SOURCE_LABELS = {
  [EFFECT_SOURCE.DB]: 'Database',
  [EFFECT_SOURCE.CODE]: 'Code',
  [EFFECT_SOURCE.NONE]: 'None',
};

const SOURCE_FETCH_CHUNK_SIZE = 12;

function getCardStatus(card, unimplementedSet) {
  if (!card.effectText?.trim()) return STATUS.NO_EFFECT;
  if (unimplementedSet.has(card.id)) return STATUS.UNIMPLEMENTED;
  return STATUS.IMPLEMENTED;
}

function BulkClearConfirmModal({
  open,
  cards,
  isLoading,
  onClose,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, isLoading]);

  if (!open) return null;

  return (
    <div className="effects-editor__modal-backdrop">
      {!isLoading && (
        <button
          type="button"
          className="effects-editor__modal-backdrop-dismiss"
          onClick={onClose}
          aria-label="Close dialog"
        />
      )}
      <div
        className="effects-editor__modal effects-editor__modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="effects-editor-bulk-clear-title"
      >
        <div className="effects-editor__modal-header">
          <h2 id="effects-editor-bulk-clear-title">Clear database effects?</h2>
          <button
            type="button"
            className="effects-editor__modal-close"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="effects-editor__modal-body">
          <p>
            This removes all saved effect versions for{' '}
            <strong>{cards.length} card{cards.length === 1 ? '' : 's'}</strong> from the database.
          </p>
          {cards.length <= 8 ? (
            <ul className="effects-editor__bulk-clear-list">
              {cards.map((card) => (
                <li key={card.id}>
                  {card.name} ({card.id})
                </li>
              ))}
            </ul>
          ) : (
            <p className="effects-editor__hint">
              Including {cards.slice(0, 3).map((c) => c.id).join(', ')} and{' '}
              {cards.length - 3} more.
            </p>
          )}
          <p className="effects-editor__hint">
            Cards with a code implementation will fall back to that afterward.
          </p>
        </div>
        <div className="effects-editor__modal-actions">
          <button
            type="button"
            className="effects-editor__btn"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="effects-editor__btn effects-editor__btn--danger"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Clearing…' : `Clear ${cards.length} from database`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EffectsEditorCardList({
  onSelectCard,
  search: controlledSearch,
  onSearchChange,
  statusFilter: controlledStatusFilter,
  onStatusFilterChange,
  sourceFilter: controlledSourceFilter,
  onSourceFilterChange,
}) {
  const [collection, setCollection] = useState([]);
  const [unimplementedIds, setUnimplementedIds] = useState([]);
  const [sourceByCardId, setSourceByCardId] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [error, setError] = useState(null);
  const [internalSearch, setInternalSearch] = useState('');
  const [internalStatusFilter, setInternalStatusFilter] = useState('all');
  const [internalSourceFilter, setInternalSourceFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [showBulkClearConfirm, setShowBulkClearConfirm] = useState(false);
  const [isBulkClearing, setIsBulkClearing] = useState(false);
  const [actionStatus, setActionStatus] = useState({ type: '', message: '' });

  const search = controlledSearch ?? internalSearch;
  const statusFilter = controlledStatusFilter ?? internalStatusFilter;
  const sourceFilter = controlledSourceFilter ?? internalSourceFilter;
  const setSearch = onSearchChange ?? setInternalSearch;
  const setStatusFilter = onStatusFilterChange ?? setInternalStatusFilter;
  const setSourceFilter = onSourceFilterChange ?? setInternalSourceFilter;

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setSourceByCardId({});
        const [cards, unimplemented] = await Promise.all([
          fetchCollection(),
          fetchUnimplementedEffectCardIds(),
        ]);
        setCollection(cards.sort((a, b) => a.id.localeCompare(b.id)));
        setUnimplementedIds(unimplemented);
      } catch (err) {
        setError(err.message || 'Failed to load cards');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (!collection.length) return undefined;

    let cancelled = false;
    const immediateSources = {};
    const cardsToFetch = [];

    for (const card of collection) {
      if (!card.effectText?.trim()) {
        immediateSources[card.id] = EFFECT_SOURCE.NONE;
      } else {
        cardsToFetch.push(card);
      }
    }

    setSourceByCardId(immediateSources);

    const loadSources = async () => {
      setIsLoadingSources(true);
      try {
        for (let i = 0; i < cardsToFetch.length; i += SOURCE_FETCH_CHUNK_SIZE) {
          if (cancelled) return;
          const chunk = cardsToFetch.slice(i, i + SOURCE_FETCH_CHUNK_SIZE);
          const entries = await Promise.all(
            chunk.map(async (card) => {
              try {
                const { source } = await getCardEffect(card.id);
                return [card.id, source];
              } catch {
                return [card.id, EFFECT_SOURCE.NONE];
              }
            }),
          );
          setSourceByCardId((prev) => ({
            ...prev,
            ...Object.fromEntries(entries),
          }));
        }
      } finally {
        if (!cancelled) setIsLoadingSources(false);
      }
    };

    loadSources();
    return () => {
      cancelled = true;
    };
  }, [collection]);

  const unimplementedSet = useMemo(() => new Set(unimplementedIds), [unimplementedIds]);

  const cardsWithStatus = useMemo(
    () => collection.map((card) => ({
      ...card,
      status: getCardStatus(card, unimplementedSet),
      effectSource: sourceByCardId[card.id] ?? null,
    })),
    [collection, unimplementedSet, sourceByCardId],
  );

  const stats = useMemo(() => ({
    total: cardsWithStatus.length,
    implemented: cardsWithStatus.filter((c) => c.status === STATUS.IMPLEMENTED).length,
    unimplemented: cardsWithStatus.filter((c) => c.status === STATUS.UNIMPLEMENTED).length,
    noEffect: cardsWithStatus.filter((c) => c.status === STATUS.NO_EFFECT).length,
    sourceDb: cardsWithStatus.filter((c) => c.effectSource === EFFECT_SOURCE.DB).length,
    sourceCode: cardsWithStatus.filter((c) => c.effectSource === EFFECT_SOURCE.CODE).length,
    sourceNone: cardsWithStatus.filter((c) => c.effectSource === EFFECT_SOURCE.NONE).length,
  }), [cardsWithStatus]);

  const filteredCards = useMemo(() => {
    const query = search.trim().toLowerCase();
    return cardsWithStatus.filter((card) => {
      if (statusFilter !== 'all' && card.status !== statusFilter) return false;
      if (sourceFilter !== 'all') {
        if (!card.effectSource || card.effectSource !== sourceFilter) return false;
      }
      if (!query) return true;
      return (
        card.id.toLowerCase().includes(query)
        || card.name.toLowerCase().includes(query)
      );
    });
  }, [cardsWithStatus, search, statusFilter, sourceFilter]);

  useEffect(() => {
    const filteredIdSet = new Set(filteredCards.map((card) => card.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => filteredIdSet.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredCards]);

  const selectedCount = selectedIds.size;

  const selectedDbCards = useMemo(
    () => cardsWithStatus.filter(
      (card) => selectedIds.has(card.id) && card.effectSource === EFFECT_SOURCE.DB,
    ),
    [cardsWithStatus, selectedIds],
  );

  const allFilteredSelected = filteredCards.length > 0
    && filteredCards.every((card) => selectedIds.has(card.id));

  const toggleSelection = useCallback((cardId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
    setLastSelectedId(cardId);
  }, []);

  const selectAllFiltered = () => {
    setSelectedIds(new Set(filteredCards.map((card) => card.id)));
    setLastSelectedId(filteredCards[filteredCards.length - 1]?.id ?? null);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  };

  const handleCardClick = (card, event) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      toggleSelection(card.id);
      return;
    }

    if (event.shiftKey && lastSelectedId) {
      event.preventDefault();
      const ids = filteredCards.map((item) => item.id);
      const start = ids.indexOf(lastSelectedId);
      const end = ids.indexOf(card.id);
      if (start >= 0 && end >= 0) {
        const [from, to] = start < end ? [start, end] : [end, start];
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (let i = from; i <= to; i += 1) next.add(ids[i]);
          return next;
        });
        setLastSelectedId(card.id);
      }
      return;
    }

    onSelectCard(card);
  };

  const bulkClearDbEffects = async () => {
    const cardsToClear = selectedDbCards;
    if (!cardsToClear.length) return;

    try {
      setIsBulkClearing(true);
      setActionStatus({ type: '', message: '' });
      const results = await Promise.allSettled(
        cardsToClear.map((card) => deleteCardEffect(card.id)),
      );
      const clearedIds = cardsToClear
        .filter((_, index) => results[index].status === 'fulfilled')
        .map((card) => card.id);
      const failedCount = cardsToClear.length - clearedIds.length;

      if (clearedIds.length) {
        const sourceEntries = await Promise.all(
          clearedIds.map(async (cardId) => {
            try {
              const { source } = await getCardEffect(cardId);
              return [cardId, source];
            } catch {
              return [cardId, EFFECT_SOURCE.NONE];
            }
          }),
        );
        setSourceByCardId((prev) => ({
          ...prev,
          ...Object.fromEntries(sourceEntries),
        }));
        setUnimplementedIds(await fetchUnimplementedEffectCardIds());
        setSelectedIds((prev) => {
          const next = new Set(prev);
          clearedIds.forEach((id) => next.delete(id));
          return next;
        });
      }

      setShowBulkClearConfirm(false);

      if (failedCount === 0) {
        setActionStatus({
          type: 'success',
          message: `Cleared database effects for ${clearedIds.length} card${clearedIds.length === 1 ? '' : 's'}.`,
        });
      } else if (clearedIds.length === 0) {
        setActionStatus({
          type: 'error',
          message: `Failed to clear database effects for ${failedCount} card${failedCount === 1 ? '' : 's'}.`,
        });
      } else {
        setActionStatus({
          type: 'error',
          message: `Cleared ${clearedIds.length} card${clearedIds.length === 1 ? '' : 's'}, but ${failedCount} failed.`,
        });
      }
    } catch (error) {
      setActionStatus({
        type: 'error',
        message: error.message || 'Failed to clear database effects',
      });
    } finally {
      setIsBulkClearing(false);
    }
  };

  return (
    <div className="effects-editor">
      <BulkClearConfirmModal
        open={showBulkClearConfirm}
        cards={selectedDbCards}
        isLoading={isBulkClearing}
        onClose={() => setShowBulkClearConfirm(false)}
        onConfirm={bulkClearDbEffects}
      />

      <header className="effects-editor__header">
        <h1>Effects Editor</h1>
        <p>
          Select a card to view or edit its effect implementation.
          Use checkboxes, Ctrl/Cmd+click, or Shift+click to select multiple cards.
        </p>
      </header>

      {error && (
        <p className="effects-editor__status effects-editor__status--error">{error}</p>
      )}

      {actionStatus.message && (
        <p className={`effects-editor__status${actionStatus.type === 'error' ? ' effects-editor__status--error' : ''}`}>
          {actionStatus.message}
        </p>
      )}

      {!error && (
        <>
          <div className="effects-editor__stats">
            <span>{stats.total} cards</span>
            <span className="effects-editor__stat effects-editor__stat--implemented">
              {stats.implemented} implemented
            </span>
            <span className="effects-editor__stat effects-editor__stat--unimplemented">
              {stats.unimplemented} not implemented
            </span>
            <span className="effects-editor__stat effects-editor__stat--no-effect">
              {stats.noEffect} no effect text
            </span>
          </div>
          <div className="effects-editor__stats effects-editor__stats--sources">
            <span className="effects-editor__stat effects-editor__stat--source-db">
              {stats.sourceDb} database
            </span>
            <span className="effects-editor__stat effects-editor__stat--source-code">
              {stats.sourceCode} code
            </span>
            <span className="effects-editor__stat effects-editor__stat--source-none">
              {stats.sourceNone} none
            </span>
            {isLoadingSources && (
              <span className="effects-editor__hint">Loading sources…</span>
            )}
          </div>

          <div className="effects-editor__list-filters">
            <input
              type="search"
              className="effects-editor__picker-search"
              placeholder="Search by name or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value={STATUS.IMPLEMENTED}>Implemented</option>
              <option value={STATUS.UNIMPLEMENTED}>Not implemented</option>
              <option value={STATUS.NO_EFFECT}>No effect text</option>
            </select>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="all">All sources</option>
              <option value={EFFECT_SOURCE.DB}>Database</option>
              <option value={EFFECT_SOURCE.CODE}>Code</option>
              <option value={EFFECT_SOURCE.NONE}>None</option>
            </select>
          </div>

          {!isLoading && filteredCards.length > 0 && (
            <div className="effects-editor__bulk-actions">
              <label className="effects-editor__checkbox-label">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={() => (allFilteredSelected ? clearSelection() : selectAllFiltered())}
                />
                Select all filtered ({filteredCards.length})
              </label>
              {selectedCount > 0 && (
                <>
                  <span className="effects-editor__bulk-count">
                    {selectedCount} selected
                  </span>
                  <button
                    type="button"
                    className="effects-editor__btn"
                    onClick={clearSelection}
                  >
                    Clear selection
                  </button>
                  <button
                    type="button"
                    className="effects-editor__btn effects-editor__btn--danger"
                    disabled={!selectedDbCards.length || isBulkClearing}
                    title={
                      selectedDbCards.length
                        ? undefined
                        : 'None of the selected cards have a database implementation'
                    }
                    onClick={() => setShowBulkClearConfirm(true)}
                  >
                    Clear database implementation
                    {selectedDbCards.length > 0 && selectedDbCards.length !== selectedCount
                      ? ` (${selectedDbCards.length})`
                      : ''}
                  </button>
                </>
              )}
            </div>
          )}

          {isLoading && <p className="effects-editor__hint">Loading cards…</p>}

          {!isLoading && (
            <div className="effects-editor__card-list">
              {filteredCards.map((card) => {
                const isSelected = selectedIds.has(card.id);
                return (
                  <div
                    key={card.id}
                    className={[
                      'effects-editor__card-list-item',
                      `effects-editor__card-list-item--${card.status}`,
                      isSelected ? 'effects-editor__card-list-item--selected' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <label className="effects-editor__card-list-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(card.id)}
                        aria-label={`Select ${card.name}`}
                      />
                    </label>
                    <button
                      type="button"
                      className="effects-editor__card-list-item-main"
                      onClick={(event) => handleCardClick(card, event)}
                    >
                      <img
                        src={getProxiedCardImageUrl(card.imgUrl)}
                        alt={card.name}
                        loading="lazy"
                      />
                      <div className="effects-editor__card-list-info">
                        <strong>{card.name}</strong>
                        <span>{card.id}</span>
                        <div className="effects-editor__card-list-badges">
                          <span className={`effects-editor__status-badge effects-editor__status-badge--${card.status}`}>
                            {card.status === STATUS.IMPLEMENTED && 'Implemented'}
                            {card.status === STATUS.UNIMPLEMENTED && 'Not implemented'}
                            {card.status === STATUS.NO_EFFECT && 'No effect text'}
                          </span>
                          {card.effectSource ? (
                            <span className={`effects-editor__source-badge effects-editor__source-badge--${card.effectSource}`}>
                              {EFFECT_SOURCE_LABELS[card.effectSource]}
                            </span>
                          ) : (
                            <span className="effects-editor__source-badge effects-editor__source-badge--loading">
                              …
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {!isLoading && filteredCards.length === 0 && (
            <p className="effects-editor__hint">No cards match your filters.</p>
          )}
        </>
      )}
    </div>
  );
}
